import React, { useEffect, useMemo, useState } from 'react';
import { Card, PageHeader, Button, Badge, Input } from '../../components/shared/ui';
import { adminAPI, extendedAdminAPI as adminAPI2 } from '../../utils/api';

const DEFAULT_SUBJECT = 'İlanınızı oluşturun — stajyer adayları sizi bekliyor';
const DEFAULT_MESSAGE = `Merhaba {{ŞirketAdı}},

Türkiye Siber Güvenlik Kümelenmesi Staj Platformu'na hoş geldiniz. Şirket hesabınız onaylandı ve artık staj ilanı yayınlayabilirsiniz.

Fark ettik ki henüz bir staj ilanı oluşturmamışsınız. Platformumuzda siber güvenlik alanında kendini geliştiren, CTF ve sertifika sahibi yüzlerce öğrenci sizi bekliyor. İlanınızı açtığınızda sistemimiz, aradığınız kriterlere (beceri, bölüm, şehir vb.) en uygun adayları otomatik olarak eşleştirip karşınıza getiriyor.

İlan oluşturmak yalnızca birkaç dakika sürüyor:
1. Platforma giriş yapın
2. "İlanlar" bölümünden "+ Yeni İlan" deyin
3. Aradığınız nitelikleri girin — gerisini sistem hallediyor

İlan oluşturmak için: https://staj.siberatolye.com/company/positions

Herhangi bir sorunuz olursa bu e-postayı yanıtlamanız yeterli; ekibimiz memnuniyetle yardımcı olur.

Türkiye Siber Güvenlik Kümelenmesi
Staj Platformu Ekibi`;

const AdminMessages = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyNoPosition, setOnlyNoPosition] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    adminAPI.getCompanies({ limit: 1000 })
      .then(r => setCompanies(r.data))
      .finally(() => setLoading(false));
  }, []);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    return companies.filter(c => {
      if (onlyNoPosition && Number(c.position_count) > 0) return false;
      if (!q) return true;
      return (c.name || '').toLocaleLowerCase('tr').includes(q) || (c.email || '').toLowerCase().includes(q);
    });
  }, [companies, search, onlyNoPosition]);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selected.has(c.id));
  const toggleAllFiltered = () => setSelected(s => {
    const n = new Set(s);
    if (allFilteredSelected) filtered.forEach(c => n.delete(c.id));
    else filtered.forEach(c => n.add(c.id));
    return n;
  });

  const handleSend = async () => {
    if (selected.size === 0) return flash('Hata: En az bir şirket seçin');
    if (!message.trim()) return flash('Hata: Mesaj boş olamaz');
    if (!window.confirm(`${selected.size} şirkete e-posta ve bildirim gönderilecek. Onaylıyor musunuz?`)) return;
    setSending(true);
    try {
      const r = await adminAPI2.messageCompanies([...selected], subject, message);
      flash(`✓ ${r.data.sent} şirkete gönderildi${r.data.failed?.length ? ` (${r.data.failed.length} başarısız)` : ''}`);
      setSelected(new Set());
    } catch (err) {
      flash('Hata: ' + (err?.response?.data?.error || 'Gönderilemedi'));
    } finally { setSending(false); }
  };

  return (
    <>
      <PageHeader title="Şirketlere Mesaj" description="Seçtiğiniz şirketlere e-posta + uygulama-içi bildirim gönderin" />

      {toast && (
        <div className="fixed bottom-20 right-6 z-[9999] px-5 py-3 rounded-xl font-semibold text-sm shadow-lg"
          style={{ background: toast.startsWith('Hata') ? '#fef2f2' : '#f0fdf4', color: toast.startsWith('Hata') ? '#dc2626' : '#16a34a', border: `1px solid ${toast.startsWith('Hata') ? '#fca5a5' : '#86efac'}` }}>
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Sol: şirket seçimi */}
        <Card padded={false} className="overflow-hidden">
          <div className="p-4 border-b border-ink-100 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-ink-900">Şirket Seç</h2>
              <span className="text-sm text-brand-700 font-medium">{selected.size} seçili</span>
            </div>
            <Input placeholder="İsim veya e-posta ara..." value={search} onChange={e => setSearch(e.target.value)} />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-ink-600 cursor-pointer">
                <input type="checkbox" checked={onlyNoPosition} onChange={e => setOnlyNoPosition(e.target.checked)} />
                Sadece ilan açmamışlar
              </label>
              <button onClick={toggleAllFiltered} className="text-sm text-brand-700 hover:underline">
                {allFilteredSelected ? 'Seçimi kaldır' : `Tümünü seç (${filtered.length})`}
              </button>
            </div>
          </div>
          <div className="max-h-[480px] overflow-y-auto divide-y divide-ink-100">
            {loading ? <div className="p-8 text-center text-ink-400">Yükleniyor...</div>
             : filtered.length === 0 ? <div className="p-8 text-center text-ink-400">Şirket bulunamadı.</div>
             : filtered.map(c => (
              <label key={c.id} className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-ink-50 ${selected.has(c.id) ? 'bg-brand-50/50' : ''}`}>
                <input type="checkbox" className="mt-1" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-ink-900 truncate">{c.name || '(isimsiz)'}</span>
                    <Badge tone={c.is_approved ? 'green' : 'amber'}>{c.is_approved ? 'Onaylı' : 'Bekliyor'}</Badge>
                    <Badge tone={Number(c.position_count) > 0 ? 'gray' : 'red'}>{Number(c.position_count) > 0 ? `${c.position_count} ilan` : 'ilan yok'}</Badge>
                  </div>
                  <div className="text-xs text-ink-400 truncate">{c.email}</div>
                </div>
              </label>
            ))}
          </div>
        </Card>

        {/* Sağ: mesaj */}
        <Card className="space-y-4">
          <h2 className="text-base font-semibold text-ink-900">Mesaj</h2>
          <Input label="Konu" value={subject} onChange={e => setSubject(e.target.value)} />
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">İçerik</label>
            <textarea rows={16} className="input resize-y font-sans text-sm leading-relaxed" value={message} onChange={e => setMessage(e.target.value)} />
            <p className="text-xs text-ink-400 mt-1.5">
              <code className="bg-ink-100 px-1 rounded">{'{{ŞirketAdı}}'}</code> yazarsanız her e-postada şirketin adıyla değiştirilir.
              Bağlantılar otomatik tıklanabilir olur.
            </p>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm text-ink-500">{selected.size} alıcı</span>
            <Button onClick={handleSend} disabled={sending || selected.size === 0}>
              {sending ? 'Gönderiliyor...' : `Gönder (${selected.size})`}
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
};

export default AdminMessages;
